import React from "react";
import { CAT_COLOR, RISK_CFG, getIndicativeCreditRange } from "../../../data/riskAssessmentData";
import type {
  C2TypeId,
  Question,
  RiskRating,
} from "../../../types/risk-assessment.types";

interface StepThreeProps {
  risk: RiskRating;
  rc: (typeof RISK_CFG)[RiskRating];
  c1Label: string;
  c2Name: string;
  c2EntityType: C2TypeId | null;
  selC2: { id: string; label: string; icon: string } | null;
  flagged: Question[];
  questions: Question[];
  reset: () => void;
  handleComplete: () => void;
  isCompleting: boolean;
}

const StepThree: React.FC<StepThreeProps> = ({
  risk,
  rc,
  c1Label,
  c2Name,
  c2EntityType,
  selC2,
  flagged,
  questions,
  reset,
  handleComplete,
  isCompleting,
}) => {
  const creditRange = getIndicativeCreditRange(c2EntityType, risk);

  return (
  <div style={{ padding: "0" }}>
    {/* ── Top strip: risk colour band ── */}
    <div
      className="ra-risk-banner"
      style={{
        background:
          risk === "High"
            ? "linear-gradient(135deg, #DC2626, #EF4444)"
            : risk === "Medium"
              ? "linear-gradient(135deg, #EA580C, #F97316)"
              : "linear-gradient(135deg, #16A34A, #22C55E)",
        padding: "32px 32px 28px",
        borderRadius: "0",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.07,
          backgroundImage:
            "radial-gradient(circle at 20% 80%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              opacity: 0.8,
              marginBottom: 6,
            }}
          >
            Preliminary Risk Rating · Section A2
          </div>
          <div
            style={{
              fontSize: 34,
              fontWeight: 900,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {rc.label}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              opacity: 0.9,
              marginTop: 6,
            }}
          >
            {rc.cdd}
          </div>
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(8px)",
            borderRadius: 14,
            padding: "14px 22px",
            textAlign: "center",
            border: "1px solid rgba(255,255,255,0.25)",
            minWidth: 90,
          }}
        >
          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            {creditRange.min}–{creditRange.max}
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              opacity: 0.85,
              marginTop: 4,
            }}
          >
            Credits Est.
          </div>
          <div
            style={{
              fontSize: 7.5,
              fontWeight: 500,
              opacity: 0.7,
              marginTop: 2,
              letterSpacing: "0.02em",
            }}
          >
            Indicative estimate
          </div>
        </div>
      </div>
    </div>

    {/* ── Body content ── */}
    <div className="ra-step3-body" style={{ padding: "24px 28px 28px" }}>
      {/* ── Summary row ── */}
      <div
        className="ra-summary-row"
        style={{
          display: "flex",
          gap: 0,
          marginBottom: 22,
          border: "1px solid #E8ECF1",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {[
          { l: "Reporting Entity", v: c1Label },
          {
            l: "Entity Type",
            v: selC2?.label || c2EntityType || "—",
          },
          { l: "Customer", v: c2Name },
          {
            l: "Indicators",
            v: `${flagged.length} / ${questions.length}`,
          },
        ].map((x, i) => (
          <div
            key={x.l}
            style={{
              flex: 1,
              padding: "14px 16px",
              borderRight: i < 3 ? "1px solid #E8ECF1" : "none",
              background: "#FAFBFD",
            }}
          >
            <div
              style={{
                color: "#94A3B8",
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 5,
              }}
            >
              {x.l}
            </div>
            <div
              style={{
                color: "#1E293B",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {x.v}
            </div>
          </div>
        ))}
      </div>

      {/* ── Required Action ── */}
      <div
        style={{
          background: rc.bg,
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 22,
          borderLeft: `4px solid ${rc.color}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 14 }}>
            {risk === "High" ? "🚨" : risk === "Medium" ? "⚠️" : "✅"}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: rc.color,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Required Action
          </span>
        </div>
        <p
          style={{
            color: "#334155",
            fontSize: 13,
            margin: 0,
            lineHeight: 1.65,
            paddingLeft: 22,
          }}
        >
          {rc.action}
        </p>
      </div>

      {/* ── Risk Indicators ── */}
      {flagged.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <span
              style={{
                color: "#374151",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Risk Indicators Identified
            </span>
            <span
              style={{
                background: rc.bg,
                color: rc.color,
                borderRadius: 20,
                padding: "3px 12px",
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {flagged.length}
            </span>
          </div>

          {(["High", "Medium"] as const).map((rating) => {
            const items = flagged.filter((q) => q.rating === rating);
            if (!items.length) return null;
            const rColor = rating === "High" ? "#EF4444" : "#F97316";
            return (
              <div key={rating} style={{ marginBottom: 6 }}>
                {items.map((q, qi) => (
                  <div
                    key={q.id}
                    style={{
                      display: "flex",
                      gap: 14,
                      padding: "14px 16px",
                      background: qi % 2 === 0 ? "#FAFBFD" : "#fff",
                      borderBottom: "1px solid #F1F3F6",
                      ...(qi === 0
                        ? {
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                          }
                        : {}),
                      ...(qi === items.length - 1
                        ? {
                            borderBottomLeftRadius: 10,
                            borderBottomRightRadius: 10,
                            borderBottom: "none",
                          }
                        : {}),
                    }}
                  >
                    {/* Severity dot */}
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: rColor,
                        flexShrink: 0,
                        marginTop: 5,
                        boxShadow: `0 0 0 3px ${rColor}20`,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: rColor,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          {rating}
                        </span>
                        <span
                          style={{
                            width: 3,
                            height: 3,
                            borderRadius: "50%",
                            background: "#CBD5E1",
                            display: "inline-block",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: CAT_COLOR[q.category] || "#6B7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {q.category}
                        </span>
                      </div>
                      <div
                        style={{
                          color: "#475569",
                          fontSize: 12.5,
                          lineHeight: 1.55,
                        }}
                      >
                        {q.text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Disclaimer ── */}
      <div
        style={{
          background: "#F8FAFC",
          borderRadius: 10,
          padding: "13px 16px",
          marginBottom: 24,
          fontSize: 11,
          color: "#94A3B8",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "#64748B" }}>Note:</strong> This is a pending
        preliminary risk rating based on C1&apos;s knowledge of the client
        (AUSTRAC Initial CDD Forms, Jan 2026). Final risk confirmed after full
        CDD verification — identity checks, sanctions screening, PEP checks
        &amp; document verification. AML/CTF obligations remain with your
        reporting entity.
      </div>

      {/* ── Action Buttons ── */}
      <div
        className="ra-action-buttons"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr",
          gap: 10,
        }}
      >
        <button
          onClick={handleComplete}
          // Disabled while the assessment is being written. Without this a second
          // press during the request wrote a second assessment, because each press
          // that finds no assessment id takes the create branch.
          disabled={isCompleting}
          style={{
            background: isCompleting
              ? "#94A3B8"
              : "linear-gradient(135deg, #1B3A6B, #2563EB)",
            border: "none",
            borderRadius: 10,
            padding: "12px 24px",
            color: "#fff",
            fontSize: 13.5,
            fontWeight: 700,
            cursor: isCompleting ? "not-allowed" : "pointer",
            letterSpacing: "0.01em",
            transition: "all 0.15s",
            boxShadow: isCompleting
              ? "none"
              : "0 2px 8px rgba(27,58,107,0.25)",
          }}
        >
          {isCompleting ? "Saving…" : "Proceed to Request C2 Link →"}
        </button>
      </div>
    </div>
  </div>
  );
};

export default StepThree;
